import {
  Alert,
  AlertIcon,
  Badge,
  Box,
  Flex,
  Spinner,
  Table,
  TableContainer,
  Tbody,
  Td,
  Text,
  Th,
  Thead,
  Tr,
  useColorModeValue,
} from "@chakra-ui/react"
import { useQuery } from "@tanstack/react-query"
import { useNavigate } from "@tanstack/react-router"
import { useEffect, useMemo } from "react"
import {
  type ProductionOrderListDTO,
  type ProductionOrderListResponseDTO,
  ProductionOrderService,
  type TechnologyListResponseDTO,
  TechnologyService,
} from "../../../client"
import { PaginationFooter } from "../../Common/PaginationFooter"
import {
  RowCheckbox,
  SelectAllCheckbox,
  useRowSelection,
} from "../selection.tsx"
import {
  getProductionOrderStatusColor,
  getProductionOrderStatusLabel,
} from "./statusUtils"

type Props = {
  page: number
  pageSize: number
  searchQuery?: string
  status?: string
  technologyId?: string
  outputProductId?: string
  onChangePage: (page: number) => void
  onSelectionChange?: (
    selectedItems: ProductionOrderListDTO[],
    selectedIds: string[],
  ) => void
}

export default function ProductionOrderList({
  page,
  pageSize,
  searchQuery,
  status,
  technologyId,
  outputProductId,
  onChangePage,
  onSelectionChange,
}: Props) {
  const navigate = useNavigate()
  const hoverBg = useColorModeValue("gray.50", "whiteAlpha.100")
  const zebraBg = useColorModeValue("blackAlpha.50", "whiteAlpha.50")
  const cardBg = useColorModeValue("white", "gray.800")
  const borderColor = useColorModeValue("gray.200", "whiteAlpha.200")
  const theadBg = useColorModeValue("gray.100", "whiteAlpha.100")
  const mutedText = useColorModeValue("gray.500", "gray.400")
  const progressTrackBg = useColorModeValue("gray.200", "whiteAlpha.300")
  const descriptionTextColor = useColorModeValue("gray.600", "gray.300")

  const { data, isPending, isError, error } =
    useQuery<ProductionOrderListResponseDTO>({
      queryKey: [
        "production-orders",
        "list",
        searchQuery ?? "",
        status ?? "",
        technologyId ?? "",
        outputProductId ?? "",
        page,
        pageSize,
      ],
      queryFn: () =>
        ProductionOrderService.getAllProductionOrders(
          searchQuery,
          status,
          technologyId,
          outputProductId,
          page,
          pageSize,
        ),
    })
  const { data: technologiesResponse } = useQuery<TechnologyListResponseDTO>({
    queryKey: ["technologies", "list", "production-order-list"],
    queryFn: () =>
      TechnologyService.getTechnologyListPage(undefined, "all", 1, 1000),
  })

  const selection = useRowSelection()

  useEffect(() => {
    if (!onSelectionChange) return
    const selectedIds = selection.selectedIds
    const selectedItems: ProductionOrderListDTO[] = (data?.items ?? []).filter(
      (i) => selection.selected.has(i.id),
    )
    try {
      onSelectionChange(selectedItems, selectedIds)
    } catch (_) {}
  }, [selection.selected, selection.selectedIds, data, onSelectionChange])

  const items = data?.items ?? []
  const total = data?.total ?? 0
  const totalPages = Math.max(1, Math.ceil(total / pageSize))
  const visibleIds = useMemo(() => items.map((i) => i.id), [items])
  const technologyMap = useMemo(() => {
    const map = new Map<string, { code?: string; name?: string }>()
    ;(technologiesResponse?.items ?? []).forEach((technology) => {
      map.set(technology.id, {
        code: technology.code,
        name: technology.name,
      })
    })
    return map
  }, [technologiesResponse?.items])
  const fmt = (d?: string) => (d ? new Date(d).toLocaleString() : "—")
  const fmtDate = (d?: string | null) =>
    d ? new Date(d).toLocaleDateString() : "—"
  const getOrderDescription = (order: ProductionOrderListDTO) => {
    const technology = technologyMap.get(order.technology_id)
    if (technology?.name && technology?.code) {
      return `${technology.code} • ${technology.name}`
    }
    if (technology?.name) return technology.name
    if (technology?.code) return technology.code
    return "Brak przypisanej technologii"
  }
  const getProgress = (produced?: number, target?: number) => {
    if (!target || target <= 0) return 0
    return Math.max(0, Math.min(100, ((produced ?? 0) / target) * 100))
  }
  const getProgressColor = (pct: number) => {
    const hue = 50 + (70 * pct) / 100
    return `hsl(${hue}, 80%, 40%)`
  }

  return (
    <Box
      bg={cardBg}
      borderWidth="1px"
      borderColor={borderColor}
      borderRadius="xl"
      overflow="hidden"
      display="flex"
      flexDirection="column"
      h="full"
      minH={0}
    >
      {isPending && (
        <Flex justify="center" align="center" w="full" h="44vh">
          <Spinner size="xl" color="main.accent" />
        </Flex>
      )}

      {isError && !isPending && (
        <Alert status="error" borderRadius="md">
          <AlertIcon />
          Nie udało się pobrać zleceń: {(error as any)?.message ?? "Błąd"}
        </Alert>
      )}

      {!isPending && !isError && items.length === 0 && (
        <Flex justify="center" align="center" w="full" h="36vh">
          <Text color={mutedText}>Brak zleceń</Text>
        </Flex>
      )}

      {!isPending && !isError && items.length > 0 && (
        <TableContainer flex="1" minH={0} overflowY="auto">
          <Table size="sm" variant="simple" sx={{ tableLayout: "fixed" }}>
            <Thead bg={theadBg} position="sticky" top={0} zIndex={1}>
              <Tr>
                <Th w="40px" p={2}>
                  <SelectAllCheckbox
                    visibleIds={visibleIds}
                    selection={selection}
                  />
                </Th>
                <Th w="320px">Numer</Th>
                <Th w="160px">Status</Th>
                <Th w="90px">Cel</Th>
                <Th w="110px">Wykonano</Th>
                <Th w="180px">Postęp</Th>
                <Th w="110px">Opakowanie</Th>
                <Th w="170px">Utworzono</Th>
                <Th w="170px">Daty</Th>
              </Tr>
            </Thead>
            <Tbody>
              {items.map((o, idx) => {
                const pct = getProgress(o.produced_qty, o.target_qty)
                const color = getProgressColor(pct)
                return (
                  <Tr
                    key={o.id}
                    bg={idx % 2 === 0 ? "transparent" : zebraBg}
                    _hover={{ bg: hoverBg }}
                    cursor="pointer"
                    onClick={() =>
                      navigate({
                        to: "/WMS/production-orders/$order",
                        params: { order: o.id },
                      })
                    }
                  >
                    <Td w="40px" p={2} onClick={(e) => e.stopPropagation()}>
                      <RowCheckbox id={o.id} selection={selection} />
                    </Td>
                    <Td verticalAlign="top">
                      <Text fontWeight="semibold">{o.number}</Text>
                      <Text
                        fontSize="xs"
                        color={descriptionTextColor}
                        mt={1}
                        noOfLines={1}
                        title={getOrderDescription(o)}
                      >
                        {getOrderDescription(o)}
                      </Text>
                    </Td>
                    <Td verticalAlign="top">
                      <Badge
                        colorScheme={getProductionOrderStatusColor(o.status)}
                        whiteSpace="nowrap"
                      >
                        {getProductionOrderStatusLabel(o.status)}
                      </Badge>
                    </Td>
                    <Td>{o.target_qty}</Td>
                    <Td>{o.produced_qty}</Td>
                    <Td>
                      <Box
                        bg={progressTrackBg}
                        borderRadius="full"
                        h="8px"
                        position="relative"
                        overflow="hidden"
                      >
                        <Box bg={color} h="8px" w={`${pct}%`} />
                      </Box>
                      <Text fontSize="xs" color={mutedText} mt={1}>
                        {pct.toFixed(0)}%
                      </Text>
                    </Td>
                    <Td>{o.batch_size}</Td>
                    <Td>{fmt(o.created_at)}</Td>
                    <Td>
                      <Text fontSize="xs" color={mutedText}>
                        Start: {fmtDate(o.start_date)}
                      </Text>
                      <Text fontSize="xs" color={mutedText}>
                        Koniec: {fmtDate(o.planned_end_date)}
                      </Text>
                    </Td>
                  </Tr>
                )
              })}
            </Tbody>
          </Table>
        </TableContainer>
      )}

      {!isPending && !isError && total > 0 && (
        <Flex
          justify="space-between"
          align={{ base: "stretch", md: "center" }}
          direction={{ base: "column", md: "row" }}
          gap={3}
          px={4}
          py={4}
          flexShrink={0}
        >
          <Text fontSize="sm" color={mutedText}>
            Pokazano {(page - 1) * pageSize + 1}-
            {(page - 1) * pageSize + items.length} z {total}
          </Text>
          <PaginationFooter
            mt={0}
            onChangePage={onChangePage}
            page={page}
            hasNextPage={page < totalPages}
            hasPreviousPage={page > 1}
          />
        </Flex>
      )}
    </Box>
  )
}
